import {useEffect,useRef,useState} from 'react';
import {Link} from 'react-router-dom';
import {ArrowUpRight} from 'lucide-react';
import {trustedCompanies} from './homePresentation';
import './HomeEnhancements.css';

export function HomeMotion({children}) {
  const root=useRef(null);
  useEffect(()=>{
    const media=matchMedia('(prefers-reduced-motion: reduce)');
    if(media.matches || !window.IntersectionObserver)return;
    const nodes=root.current.querySelectorAll('[data-home-reveal]');
    const observer=new IntersectionObserver(entries=>entries.forEach(({target,isIntersecting})=>{if(isIntersecting){target.classList.remove('home-await');observer.unobserve(target);}}),{threshold:.08});
    nodes.forEach(node=>{node.classList.add('home-await');observer.observe(node);});
    const clear=()=>{observer.disconnect();nodes.forEach(node=>node.classList.remove('home-await'));};
    const changed=()=>{if(media.matches)clear();};media.addEventListener('change',changed);
    return()=>{clear();media.removeEventListener('change',changed);};
  },[]);
  return <div ref={root} className="home-pass-two">{children}</div>;
}

export function RealCounter({value,label}) {
  const element=useRef(null),played=useRef(false),[display,setDisplay]=useState(value);
  useEffect(()=>{
    const media=matchMedia('(prefers-reduced-motion: reduce)');let frame,observer;
    setDisplay(value);
    if(!played.current&&!media.matches&&window.IntersectionObserver){
      observer=new IntersectionObserver(entries=>{if(!entries.some(e=>e.isIntersecting))return;
        played.current=true;observer.disconnect();const start=performance.now();
        const tick=now=>{const t=Math.min(1,(now-start)/1000);setDisplay(Math.round(value*(1-Math.pow(1-t,3))));if(t<1)frame=requestAnimationFrame(tick);};
        frame=requestAnimationFrame(tick);
      },{threshold:.4});observer.observe(element.current);
    }
    const stop=()=>{cancelAnimationFrame(frame);observer?.disconnect();setDisplay(value);};
    const changed=()=>{if(media.matches){played.current=true;stop();}};media.addEventListener('change',changed);
    return()=>{cancelAnimationFrame(frame);observer?.disconnect();media.removeEventListener('change',changed);};
  },[value]);
  return <div ref={element} className="home-counter" aria-label={`${value.toLocaleString()} ${label}`}><strong aria-hidden="true">{display.toLocaleString()}</strong><span>{label}</span></div>;
}

export function TrustedProfessionals({section}) {
  const rows=trustedCompanies(section.items);
  if(!section.authorized||!rows.length)return null;
  return <><h2 data-home-reveal>{section.heading && section.heading!=='Partners' ? section.heading : 'Trusted by Industry Professionals'}</h2><p className="site-body" data-home-reveal>{section.body||'Early participating manufacturers and industry partners helping shape the first CARATJEWEL EXPO.'}</p><div className="home-trust-grid">{rows.map((row,i)=>{
    const content=<>{row.logo&&<img src={row.logo} alt="" loading="lazy" decoding="async" onError={e=>{e.currentTarget.hidden=true;}}/>}<strong>{row.name}</strong>{(row.founding||row.role)&&<span>{row.founding?'Founding Exhibitor':row.role}</span>}</>;
    return <div key={i} className="home-trust-item" data-home-reveal style={{'--home-delay':`${i%4*60}ms`}}>{row.website?<a href={row.website} rel="noopener noreferrer">{content}</a>:content}</div>;
  })}</div></>;
}

const steps=[
  ['Discover / Register','Explore the platform and create the right buyer or exhibitor account.'],
  ['Verify or Apply','Buyers submit business details for verification; exhibitors apply to participate.'],
  ['Explore / Exhibit','Browse exhibitions and digital booths, or present products through a digital catalogue.'],
  ['Connect / Meet','Send an inquiry and arrange a focused private business meeting.']
];
export function HomeSteps(){return <ol className="home-steps">{steps.map(([title,copy],i)=><li key={title} data-home-reveal style={{'--home-delay':`${i*65}ms`}}><span className="home-step-number">0{i+1}</span><h3>{title}</h3><p>{copy}</p></li>)}</ol>;}

const benefits={
  buyers:[
    ['Discover manufacturers and products','Find jewellery manufacturers, exhibitors and collections in one focused marketplace.'],
    ['Verified exhibitor environment','Explore businesses presented within a professional exhibition setting.'],
    ['Browse exhibitions and catalogues','Move from show discovery to digital booths and product catalogues.'],
    ['Request private meetings','Turn relevant discoveries into focused one-to-one business conversations.'],
    ['Join exhibition interactions','Take part in scheduled exhibition experiences when they are available.']
  ],
  exhibitors:[
    ['Digital exhibition presence','Present your business through a dedicated digital booth.'],
    ['Product catalogue','Organize collections so professional buyers can explore them clearly.'],
    ['Global buyer reach','Make your offering discoverable to buyers beyond your existing network.'],
    ['Buyer inquiries','Receive interest from buyers exploring your company and products.'],
    ['Private meetings','Move promising inquiries into focused business meetings.'],
    ['LIVE exhibition capability','Showcase products during eligible scheduled exhibition sessions when available.']
  ]
};

export function HomeBenefits({audience}){
  return <ul className="home-benefit-grid" data-testid={`home-${audience}-benefits`}>{benefits[audience].map(([title,copy],i)=><li key={title} data-home-reveal style={{'--home-delay':`${i%3*55}ms`}}><h3>{title}</h3><p>{copy}</p></li>)}</ul>;
}

export function VerifiedBuyerDetails(){
  return <div className="home-verified-details" data-testid="home-verified-details">
    <article data-home-reveal><span>01</span><h3>Business review</h3><p>Applicants provide business information so buyer access reflects a professional sourcing purpose.</p></article>
    <article data-home-reveal style={{'--home-delay':'60ms'}}><span>02</span><h3>More relevant connections</h3><p>Verification helps exhibitors respond to credible interest and supports focused marketplace conversations.</p></article>
  </div>;
}

export function UpcomingShow(){
  return <div id="upcoming-show" className="home-upcoming-show" data-testid="home-upcoming-show" data-home-reveal>
    <div><span className="home-upcoming-brand">CARATJEWEL EXPO</span><h2>Diamond Jewellery Show</h2></div>
    <div className="home-upcoming-status"><span>Upcoming Show</span><strong>Schedule Announcing Soon</strong></div>
  </div>;
}

export function HomeClosing({section}) {
  const target=section.items?.find(item=>item.startsWith('Target:'));
  return <><div className="home-urgency" data-home-reveal><span>{section.heading && !['Make your next connection','final'].includes(section.heading)?section.heading:'LIMITED BOOTHS AVAILABLE'}</span><p>{section.body && section.body!=='Explore the catalogue or apply to exhibit.'?section.body:'Secure your place for the upcoming CARATJEWEL EXPO.'}</p><Link to="/exhibit" className="home-cta">Reserve Your Booth<ArrowUpRight size={16}/></Link></div>{target&&<p className="home-target" data-home-reveal>{target}</p>}<div className="home-split-cta"><article data-home-reveal><span className="eyebrow">FOR EXHIBITORS</span><h2>Grow beyond borders.</h2><p>Showcase your jewellery, go live, meet professional buyers and grow beyond borders.</p><Link className="home-cta" to="/exhibit">Reserve Your Booth<ArrowUpRight size={16}/></Link></article><article data-home-reveal style={{'--home-delay':'70ms'}}><span className="eyebrow">FOR BUYERS</span><h2>Your next connection.</h2><p>Discover manufacturers, explore collections and request private meetings.</p><Link className="home-cta" to="/verify">Apply as Verified Buyer<ArrowUpRight size={16}/></Link></article></div></>;
}
